import React, { useState } from 'react';
import styled from 'styled-components';

import {
  useInvalidateDelegatedKeysQueries,
  useMutateCreateDelegatedKey,
} from 'api/queries/delegatedKeysQueries';
import SpinningRoundedBorder from 'components/basic/animations/SpinningRoundedBorder';
import TextInput from 'components/basic/inputs/TextInput';
import ModalContent from 'components/basic/modal/components/ModalContent';
import ModalFooterButtons from 'components/basic/modal/components/ModalFooterButtons';
import SimpleModalOrMobilePage from 'components/basic/modal/SimpleModalOrMobilePage';
import ErrorPanel from 'components/basic/panels/ErrorPanel';
import Spacer from 'components/s2/Spacer/Spacer';
import { makeEthersErrorHumanRedable } from 'lib/ethereum/ethersErrors';

import DelegatedKeyConsentModal from './DelegatedKeyConsent';
import WalletAuthDetails from './WalletAuthDetails';

const Heading = styled.div`
  font-size: var(--medium2);
  padding-bottom: 16px;
`;

export default function CreateDelegatedKeyModal({
  onClose,
}: {
  onClose: () => void;
}) {
  const createDelegatedKey = useMutateCreateDelegatedKey();
  const { invalidateDelegatedKeys } = useInvalidateDelegatedKeysQueries();

  const [delegatedName, setDelegatedName] = useState('');

  const [haveConsent, setHaveConsent] = useState(false);
  const [error, setError] = useState('');
  const [delegatedKeyResult, setDelegatedKeyResult] = useState<
    | {
        delegatedPrivateKey: string;
        delegatedNonce: string;
      }
    | undefined
  >(undefined);

  return (
    <SimpleModalOrMobilePage
      title="Create Session Key"
      onClose={onClose}
      size="small"
      padding="0"
      verticalAlignOnMobile="ceneteredDiv"
    >
      {createDelegatedKey.isPending ? (
        <ModalContent>
          <Spacer height={100} />
          <SpinningRoundedBorder size={100} />
          <Spacer height={200} />
        </ModalContent>
      ) : !haveConsent ? (
        <DelegatedKeyConsentModal onContinue={() => setHaveConsent(true)} />
      ) : !delegatedKeyResult ? (
        <form
          onSubmit={async event => {
            event.preventDefault();
            try {
              const newDelegation = await createDelegatedKey.mutateAsync({
                delegatedName,
              });

              setDelegatedKeyResult({
                delegatedPrivateKey: newDelegation.delegatedWalletPrivateKey,
                delegatedNonce: newDelegation.delegatedNonce,
              });
            } catch (error) {
              console.warn(error);
              setError(makeEthersErrorHumanRedable(error));
            } finally {
              invalidateDelegatedKeys();
            }
          }}
        >
          <ModalContent>
            <Heading>Name your Session Key</Heading>

            <TextInput
              placeholder="e.g. Home Office"
              value={delegatedName}
              onChange={name => setDelegatedName(name)}
              required
            />

            {error && <ErrorPanel message={error} />}
          </ModalContent>
          <ModalFooterButtons
            confirmButtonProps={{
              submit: true,
              children: 'Save',
            }}
          />
        </form>
      ) : (
        <ModalContent>
          <WalletAuthDetails
            delegatedPrivateKey={delegatedKeyResult.delegatedPrivateKey}
            delegatedNonce={delegatedKeyResult.delegatedNonce}
          />
        </ModalContent>
      )}
    </SimpleModalOrMobilePage>
  );
}
